import React from 'react';

import RadioButton, { RadioButtonGroup } from 'carbon-react/lib/components/radio-button';
import Box from 'carbon-react/lib/components/box';
import { UICarbonNumberField } from '@futrli/ui-components';

import { RangeFilterFieldConfig } from '../types';

interface RangeFilterFieldProps {
  config: RangeFilterFieldConfig;
  onToggle: (key: string, isActive: boolean) => void;
  onFromChange: (key: string, value: string) => void;
  onToChange: (key: string, value: string) => void;
}

/**
 * Component for rendering a range filter field in the filters sidebar, allowing users to toggle the filter on/off and input a range of values when active
 * @param config - The configuration for the range filter field
 * @param onToggle - Function to call when the filter is toggled on/off
 * @param onFromChange - Function to call when the "from" value is changed
 * @param onToChange - Function to call when the "to" value is changed
 * @returns A JSX element representing the range filter field
 */
export const RangeFilterField = ({ config, onToggle, onFromChange, onToChange }: RangeFilterFieldProps) => {
  const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    onToggle(config.key, event.target.value === 'filter');
  };

  const handleFromChange = (value: string) => {
    onFromChange(config.key, value);
  };

  const handleToChange = (value: string) => {
    onToChange(config.key, value);
  };

  return (
    <Box mb={3}>
      <RadioButtonGroup
        name={`filter-range-${config.key}`}
        onChange={handleToggle}
        value={config.isActive ? 'filter' : 'all'}
        legend={config.label}
        size="medium"
      >
        <RadioButton value="all" label={config.showAllLabel} checked={!config.isActive} />
        <RadioButton value="filter" label={config.filterByRangeLabel} checked={config.isActive} />
      </RadioButtonGroup>
      {config.isActive && (
        <div style={{ paddingLeft: 16, borderLeft: '2px solid  #CCD6DB' }}>
          <Box display="flex" flexDirection="row" mt={2}>
            <Box mr={2} flex="1">
              <UICarbonNumberField
                label={config.fromLabel}
                value={config.from || ''}
                onChange={handleFromChange}
                autoComplete="off"
                currencySymbol={config.currencySymbol}
                type={config.currencySymbol ? 'currency' : undefined}
                mb={0}
              />
            </Box>
            <Box flex="1">
              <UICarbonNumberField
                label={config.toLabel}
                value={config.to || ''}
                onChange={handleToChange}
                autoComplete="off"
                currencySymbol={config.currencySymbol}
                type={config.currencySymbol ? 'currency' : undefined}
                mb={0}
              />
            </Box>
          </Box>
        </div>
      )}
    </Box>
  );
};
